import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ProjectService } from '../../services/project.service';
import { HttpEventType, HttpResponse } from '@angular/common/http';

export interface BlockScheduleSearchModel {
  projectNumber: string;
  shipDateFrom: Date | null;
  shipDateTo: Date | null;
  integratorId: number | null;
  shipToLocation: string;
  arrivalDateFrom: Date | null;
  arrivalDateTo: Date | null;
  overDimension: boolean;
  overWeight: boolean;
}

@Component({
  selector: 'app-blockschedule-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DatePickerModule,
    SelectModule,
    CheckboxModule,
    InputTextModule,
    TableModule,
    Toast
  ],
  providers: [MessageService],
  templateUrl: './blockschedule-report.html',
  styleUrl: './blockschedule-report.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BlockscheduleReportComponent implements OnInit {
  searchModel: BlockScheduleSearchModel = this.emptySearch();
  reportData: any[] = [];
  integratorList: any[] = [];
  defaultIntegrator = { integratorId: null, name: 'All' };
  loading = false;
  exporting = false;
  searched = false;

  constructor(
    @Optional() public config: DynamicDialogConfig,
    @Optional() public ref: DynamicDialogRef,
    private projectService: ProjectService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadIntegrators();
  }

  private emptySearch(): BlockScheduleSearchModel {
    return {
      projectNumber: '',
      shipDateFrom: null,
      shipDateTo: null,
      integratorId: null,
      shipToLocation: '',
      arrivalDateFrom: null,
      arrivalDateTo: null,
      overDimension: false,
      overWeight: false
    };
  }

  loadIntegrators(): void {
    this.projectService.getIntegratorList().subscribe({
      next: (list) => {
        this.integratorList = [this.defaultIntegrator, ...list];
        this.cdr.markForCheck();
      },
      error: () => {
        this.integratorList = [this.defaultIntegrator];
        this.cdr.markForCheck();
      }
    });
  }

  onSearch(): void {
    this.loading = true;
    this.searched = true;
    this.cdr.markForCheck();

    this.projectService.searchBlockScheduleReport(this.searchModel).subscribe({
      next: (data) => {
        this.reportData = data || [];
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load report data.' });
        this.cdr.markForCheck();
      }
    });
  }

  onClear(): void {
    this.searchModel = this.emptySearch();
    this.reportData = [];
    this.searched = false;
    this.cdr.markForCheck();
  }

  onExport(): void {
    if (!this.reportData.length) {
      this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'Please search first before exporting.' });
      return;
    }

    this.exporting = true;
    this.cdr.markForCheck();

    this.projectService.downloadBlockScheduleReport(this.searchModel).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.Response) {
          const data = event as HttpResponse<Blob>;
          const blob = new Blob([data.body as BlobPart], { type: data.body?.type });
          if (blob.type) {
            const a = document.createElement('a');
            a.style.display = 'none';
            document.body.appendChild(a);
            a.download = 'BlockScheduleReport.xlsx';
            a.href = URL.createObjectURL(blob);
            a.click();
            document.body.removeChild(a);
          }
          this.exporting = false;
          this.cdr.markForCheck();
        }
      },
      error: () => {
        this.exporting = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to export report.' });
        this.cdr.markForCheck();
      }
    });
  }

  onClose(): void {
    this.ref?.close();
  }
}
